import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Slide,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Close as CloseIcon
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" in={props.open} ref={ref} {...props} />;
});

export default function AddSettlement({ open, onClose }) {
  const [openDialog, setOpenDialog] = useState(false);

  // Form state
  const [settleData, setSettleData] = useState({
    employeeId: '',
    amount: '',
    description: '',
    paymentMethod: '',
    settleDate: ''
  });

  const handleInputChange = (field, value) => {
    setSettleData({
      ...settleData,
      [field]: value
    });
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setOpenDialog(false);
    }
  };

  // Define reusable input styling - copied from RecJobs for consistency
  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiSelect-select": {
        color: "#424242",
      },
      "& .MuiInputBase-input": {
        color: "#424242",
      },
    },
  };

  const checkboxStyle = {
    color: "#9e9e9e",
    "&.Mui-checked": {
      color: "#4285F4",
    },
  };

  // Use either provided open state or internal state
  const isOpen = open !== undefined ? open : openDialog;

  return (
    <div>
      {/* Only render the button if no open prop is provided (standalone mode) */}
      {open === undefined && (
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleClickOpen}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Settlement
          </Button>
        </Stack>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="lg"
        PaperProps={{
          style: {
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            margin: 0,
            borderRadius: 0,
          }
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "100%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
              Settlement Details
            </Typography>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Close
            </Button>
          </Box>

          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  {/* First row */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Employee ID <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter Employee ID"
                      size="small"
                      value={settleData.employeeId}
                      onChange={(e) => handleInputChange('employeeId', e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Employee Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      defaultValue=""
                      size="small"
                      InputProps={inputProps}
                    >
                      <MenuItem value="">Select Employee</MenuItem>
                      <MenuItem value="john">John Doe</MenuItem>
                      <MenuItem value="jane">Jane Smith</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Department
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      defaultValue=""
                      size="small"
                      InputProps={inputProps}
                    >
                      <MenuItem value="">Select Department</MenuItem>
                      <MenuItem value="hr">HR</MenuItem>
                      <MenuItem value="finance">Finance</MenuItem>
                      <MenuItem value="it">IT</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Second row */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Settlement Amount <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="0.00"
                      size="small"
                      type="number"
                      value={settleData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      InputProps={{
                        ...inputProps,
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Payment Method <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={settleData.paymentMethod}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      size="small"
                      InputProps={inputProps}
                    >
                      <MenuItem value="">Select Payment Method</MenuItem>
                      <MenuItem value="bank">Bank Transfer</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="check">Check</MenuItem>
                      <MenuItem value="online">Online Payment</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Settlement Date <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      value={settleData.settleDate}
                      onChange={(e) => handleInputChange('settleDate', e.target.value)}
                      InputProps={{
                        ...inputProps,
                        sx: {
                          ...inputProps.sx,
                          "& .MuiInputBase-input": {
                            color: "#424242",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Third row */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Status
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      defaultValue="pending"
                      size="small"
                      InputProps={inputProps}
                    >
                      <MenuItem value="pending">
                        <Box display="flex" alignItems="center">
                          <Box
                            component="span"
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "#ff9800",
                              display: "inline-block",
                              mr: 1,
                            }}
                          />
                          Pending
                        </Box>
                      </MenuItem>
                      <MenuItem value="completed">
                        <Box display="flex" alignItems="center">
                          <Box
                            component="span"
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "#4caf50",
                              display: "inline-block",
                              mr: 1,
                            }}
                          />
                          Completed
                        </Box>
                      </MenuItem>
                      <MenuItem value="rejected">
                        <Box display="flex" alignItems="center">
                          <Box
                            component="span"
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "#f44336",
                              display: "inline-block",
                              mr: 1,
                            }}
                          />
                          Rejected
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Settlement Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      defaultValue=""
                      size="small"
                      InputProps={inputProps}
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem value="salary">Salary Settlement</MenuItem>
                      <MenuItem value="reimbursement">Reimbursement</MenuItem>
                      <MenuItem value="bonus">Bonus</MenuItem>
                      <MenuItem value="severance">Severance</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mt: 4 }}>
                      <FormControlLabel
                        control={<Checkbox sx={checkboxStyle} />}
                        label="Notify Employee"
                        sx={{
                          color: "#757575",
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Description
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Enter settlement details here..."
                      value={settleData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      variant="outlined"
                      sx={{
                        "& fieldset": {
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                        },
                        "& .MuiInputBase-root": {
                          color: "#212121",
                        },
                        bgcolor: "#f9f9f9",
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Form buttons */}
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    fontWeight: 500,
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    borderColor: "#bdbdbd",
                    color: "#424242",
                    "&:hover": {
                      borderColor: "#9e9e9e",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Dialog>
    </div>
  );
}

